import React, { useState, useEffect, useRef } from 'react';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { TextBoxComponent, NumericTextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { ButtonComponent, CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { GanttVisualizationProfileControllerService, type GanttVisualizationProfileDto, MaterialControllerService, ResourceControllerService, type MaterialDto, type ResourceDto } from '../../api';
import { useTranslation } from 'react-i18next';
import type { CrudFormProps } from '../../components/CrudPage';
import { SelectableListComponent } from '../../components/SelectableListComponent';
import { ReactTabs } from '../../components/ReactTabs';

// Type with required id
type GanttProfileWithId = GanttVisualizationProfileDto & { id: string };

const GanttProfileFormDialog: React.FC<CrudFormProps<GanttProfileWithId>> = ({
  value,
  onClose,
  onSave,
  isClone
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<'USER' | 'MANDANT' | 'GLOBAL'>('USER');
  const [isDefault, setIsDefault] = useState(false);
  const [profile, setProfile] = useState<GanttVisualizationProfileDto | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  // Color Configuration State
  const [colorStrategy, setColorStrategy] = useState<'PRODUCT' | 'ORDER' | 'RESOURCE'>('PRODUCT');
  
  // Entity color mappings
  type ColorMapping = {
    uuid: string;
    name: string;
    colorMode: 'AUTO' | 'MANUAL';
    color?: string;
    autoColorIndex?: number;
  };
  const [productColorMappings, setProductColorMappings] = useState<ColorMapping[]>([]);
  const [resourceColorMappings, setResourceColorMappings] = useState<ColorMapping[]>([]);
  
  // Loaded entities
  const [, setMaterials] = useState<MaterialDto[]>([]);
  type ResourceWithSelection = ResourceDto & { selected: boolean };
  const [resources, setResources] = useState<ResourceWithSelection[]>([]);
  const [loadingEntities, setLoadingEntities] = useState(false);

  // Resource Configuration State
  const [resourceFilterType, setResourceFilterType] = useState<'ALL' | 'SELECTED' | 'BY_TYPE' | 'BY_ATTRIBUTE'>('ALL');
  // Store saved selected UUIDs to apply after resources are loaded
  const savedSelectedResourceUuidsRef = useRef<string[]>([]);

  // Display Configuration State
  const [taskLabelEnabled, setTaskLabelEnabled] = useState(true);
  const [taskLabelTemplate, setTaskLabelTemplate] = useState('{operationName}');
  const [taskLabelPosition, setTaskLabelPosition] = useState<'INSIDE' | 'ABOVE' | 'BELOW' | 'TOOLTIP_ONLY'>('INSIDE');
  const [statusIndicatorEnabled, setStatusIndicatorEnabled] = useState(true);
  const [timeScaleInterval, setTimeScaleInterval] = useState(60);
  const [showWeekend, setShowWeekend] = useState(true);
  const [showHolidays, setShowHolidays] = useState(true);
  const [showShifts, setShowShifts] = useState(true);

  // Tooltip Configuration State
  const [tooltipEnabled, setTooltipEnabled] = useState(true);
  const [tooltipTrigger, setTooltipTrigger] = useState<'HOVER' | 'CLICK' | 'BOTH'>('HOVER');
  const [tooltipDelay, setTooltipDelay] = useState(300);

  // Interaction Configuration State
  const [allowTaskEditing, setAllowTaskEditing] = useState(false);
  const [allowTaskMoving, setAllowTaskMoving] = useState(false);
  const [allowTaskResizing, setAllowTaskResizing] = useState(false);
  const [onClickAction, setOnClickAction] = useState<'NONE' | 'SELECT' | 'HIGHLIGHT'>('SELECT');

  useEffect(() => {
    if (value) {
      if (isClone) {
        // Clone: create new profile based on existing one
        setProfile({
          ...value,
          uuid: undefined,
          name: `${value.name} (Kopie)`,
          isDefault: false
        });
        setName(`${value.name} (Kopie)`);
        setDescription(value.description || '');
        setVisibility((value.visibility as 'USER' | 'MANDANT' | 'GLOBAL') || 'USER');
        setIsDefault(false);
        loadConfigurationFromProfile(value);
      } else {
        // Edit: load existing profile
        loadProfile(value.uuid || '');
      }
    } else {
      // New: create default profile
      const defaultProfile = {
        name: '',
        description: '',
        isDefault: false,
        visibility: 'USER',
        colorConfiguration: getDefaultColorConfiguration(),
        resourceConfiguration: getDefaultResourceConfiguration(),
        displayConfiguration: getDefaultDisplayConfiguration(),
        tooltipConfiguration: getDefaultTooltipConfiguration(),
        interactionConfiguration: getDefaultInteractionConfiguration()
      };
      setProfile(defaultProfile);
      setName('');
      setDescription('');
      setVisibility('USER');
      setIsDefault(false);
      loadConfigurationFromProfile(defaultProfile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadProfile and loadConfigurationFromProfile are local functions; including them would trigger on every render
  }, [value, isClone]);

  // Load entities based on color strategy
  useEffect(() => {
    const loadEntities = async () => {
      if (colorStrategy === 'PRODUCT') {
        setLoadingEntities(true);
        try {
          const materialsData = await MaterialControllerService.findAll();
          setMaterials(materialsData);
          
          const products = materialsData.filter(m => m.productionType === 'MAKE');
          setProductColorMappings(prev => {
            if (prev.length > 0) return prev; // Keep existing mappings
            return products.map((product, index) => ({
              uuid: product.uuid || '',
              name: product.name || '',
              colorMode: 'AUTO' as const,
              autoColorIndex: index
            }));
          });
        } catch (error) {
          console.error('Failed to load materials:', error);
        } finally {
          setLoadingEntities(false);
        }
      } else if (colorStrategy === 'RESOURCE') {
        setLoadingEntities(true);
        try {
          const resourcesData = await ResourceControllerService.findAll();
          // Preserve existing selections when loading resources
          setResources(prev => {
            const existingSelections = new Map(prev.map(r => [r.uuid, r.selected]));
            return resourcesData.map(resource => ({
              ...resource,
              selected: existingSelections.get(resource.uuid || '') || false
            }));
          });
          
          setResourceColorMappings(prev => {
            if (prev.length > 0) return prev; // Keep existing mappings
            return resourcesData.map((resource, index) => ({
              uuid: resource.uuid || '',
              name: resource.name || '',
              colorMode: 'AUTO' as const,
              autoColorIndex: index
            }));
          });
        } catch (error) {
          console.error('Failed to load resources:', error);
        } finally {
          setLoadingEntities(false);
        }
      } else if (colorStrategy === 'ORDER') {
        // ORDER strategy doesn't need to load entities, colors are assigned automatically
        setProductColorMappings([]);
        setResourceColorMappings([]);
      }
    };
    
    loadEntities();
     
  }, [colorStrategy]);

  // Load resources when filter type is not ALL
  useEffect(() => {
    const loadResources = async () => {
      if (resourceFilterType !== 'ALL') {
        setLoadingEntities(true);
        try {
          const resourcesData = await ResourceControllerService.findAll();
          // Apply saved selections from profile when loading resources
          const savedUuids = savedSelectedResourceUuidsRef.current;
          setResources(resourcesData.map(resource => ({
            ...resource,
            selected: savedUuids.includes(resource.uuid || '')
          })));
        } catch (error) {
          console.error('Failed to load resources:', error);
        } finally {
          setLoadingEntities(false);
        }
      } else {
        // When filter is ALL, clear resources but preserve selections if any exist
        setResources(prev => {
          if (prev.length === 0) return [];
          // Keep existing resources with their selections
          return prev;
        });
      }
    };
    
    loadResources();
  }, [resourceFilterType]);


  const loadConfigurationFromProfile = (profileData: GanttVisualizationProfileDto) => {
    // Color Configuration
    if (profileData.colorConfiguration) {
      const colorConfig = profileData.colorConfiguration as any;
      setColorStrategy(colorConfig.colorStrategy || 'PRODUCT');
      
      // Load color mappings
      if (colorConfig.productColors) {
        setProductColorMappings(colorConfig.productColors);
      }
      if (colorConfig.resourceColors) {
        setResourceColorMappings(colorConfig.resourceColors);
      }
    }

    // Resource Configuration
    if (profileData.resourceConfiguration) {
      const resourceConfig = profileData.resourceConfiguration as any;
      const filterType = resourceConfig.resourceFilter?.type || 'ALL';
      const savedSelectedUuids = resourceConfig.resourceFilter?.selectedResourceUuids || [];
      
      // Store saved UUIDs to apply after resources are loaded
      savedSelectedResourceUuidsRef.current = savedSelectedUuids;
      
      // Set filter type - this will trigger resource loading
      setResourceFilterType(filterType);
      
      // If resources are already loaded, apply selections immediately
      setResources(prev => {
        if (prev.length > 0) {
          return prev.map(r => ({
            ...r,
            selected: savedSelectedUuids.includes(r.uuid || '')
          }));
        }
        return prev;
      });
    }

    // Display Configuration
    if (profileData.displayConfiguration) {
      const displayConfig = profileData.displayConfiguration as any;
      setTaskLabelEnabled(displayConfig.taskLabel?.enabled !== false);
      setTaskLabelTemplate(displayConfig.taskLabel?.template || '{operationName}');
      setTaskLabelPosition(displayConfig.taskLabel?.position || 'INSIDE');
      setStatusIndicatorEnabled(displayConfig.statusIndicator?.enabled !== false);
      setTimeScaleInterval(displayConfig.timeScale?.interval || 60);
      setShowWeekend(displayConfig.timeScale?.showWeekend !== false);
      setShowHolidays(displayConfig.timeScale?.showHolidays !== false);
      setShowShifts(displayConfig.timeScale?.showShifts !== false);
    }

    // Tooltip Configuration
    if (profileData.tooltipConfiguration) {
      const tooltipConfig = profileData.tooltipConfiguration as any;
      setTooltipEnabled(tooltipConfig.enabled !== false);
      setTooltipTrigger(tooltipConfig.trigger || 'HOVER');
      setTooltipDelay(tooltipConfig.delay || 300);
    }

    // Interaction Configuration
    if (profileData.interactionConfiguration) {
      const interactionConfig = profileData.interactionConfiguration as any;
      setAllowTaskEditing(interactionConfig.allowTaskEditing || false);
      setAllowTaskMoving(interactionConfig.allowTaskMoving || false);
      setAllowTaskResizing(interactionConfig.allowTaskResizing || false);
      setOnClickAction(interactionConfig.onClickAction || 'SELECT');
    }
  };

  const loadProfile = async (uuid: string) => {
    try {
      setLoading(true);
      const data = await GanttVisualizationProfileControllerService.findByUuid(uuid);
      setProfile(data);
      setName(data.name || '');
      setDescription(data.description || '');
      setVisibility((data.visibility as 'USER' | 'MANDANT' | 'GLOBAL') || 'USER');
      setIsDefault(data.isDefault || false);
      loadConfigurationFromProfile(data);
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setNameError(t('validation.required'));
      return;
    }

    try {
      setNameError(null);
      setLoading(true);
      
      // Build configuration objects from state
      const colorConfiguration = {
        colorStrategy,
        defaultColorPalette: [
          '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFE66D',
          '#FF9FF3', '#54A0FF', '#5F27CD', '#00D2D3', '#FF9F43',
          '#10AC84', '#EE5A6F', '#C44569', '#F8B500', '#6C5CE7',
          '#A29BFE', '#FD79A8', '#FDCB6E', '#E17055', '#00B894'
        ],
        productColors: colorStrategy === 'PRODUCT' ? productColorMappings : undefined,
        resourceColors: colorStrategy === 'RESOURCE' ? resourceColorMappings : undefined,
        statusColors: {
          PLANNED: '#2196F3',
          IN_PROGRESS: '#4CAF50',
          COMPLETED: '#8BC34A',
          DELAYED: '#F44336',
          CANCELLED: '#9E9E9E'
        }
      };

      // Build resource configuration
      // Preserve existing resourceConfiguration if available, only update what's changed
      const existingResourceConfig = profile?.resourceConfiguration as any;
      const selectedResourceUuids = resourceFilterType === 'SELECTED'
        ? resources.filter(r => r.selected).map(r => r.uuid).filter((uuid): uuid is string => !!uuid)
        : existingResourceConfig?.resourceFilter?.selectedResourceUuids || [];
      
      // If SELECTED is chosen but no resources are selected, preserve existing selection or use empty array
      // But we should warn the user - for now, we'll preserve existing if available
      const finalSelectedUuids = resourceFilterType === 'SELECTED' && selectedResourceUuids.length === 0
        ? (existingResourceConfig?.resourceFilter?.selectedResourceUuids || [])
        : selectedResourceUuids;
      
      const resourceConfiguration = {
        resourceFilter: {
          type: resourceFilterType,
          // Only include selectedResourceUuids if type is SELECTED
          ...(resourceFilterType === 'SELECTED'
            ? { selectedResourceUuids: finalSelectedUuids }
            : {})
          // Note: filterByType and filterByAttribute are not yet implemented in the UI
          // They will be added when BY_TYPE and BY_ATTRIBUTE filter types are fully implemented
        }
      };

      const displayConfiguration = {
        taskLabel: {
          enabled: taskLabelEnabled,
          template: taskLabelTemplate,
          position: taskLabelPosition,
          fontSize: 12,
          maxLength: 50
        },
        statusIndicator: {
          enabled: statusIndicatorEnabled,
          position: 'LEFT',
          size: 'SMALL',
          showBorder: true
        },
        progressIndicator: {
          enabled: false,
          type: 'BAR',
          color: '#4CAF50'
        },
        dependencyLines: {
          enabled: false,
          style: 'SOLID',
          color: '#666',
          showArrows: true,
          thickness: 1
        },
        timeScale: {
          interval: timeScaleInterval,
          showWeekend,
          showHolidays,
          showShifts
        },
        backgroundVisualization: {
          holidays: {
            enabled: true,
            displayMode: 'BACKGROUND_COLOR',
            color: '#ffebee',
            opacity: 0.5,
            showTooltip: true
          },
          shifts: {
            enabled: true,
            displayMode: 'BAND',
            opacity: 0.3,
            showShiftLabels: false,
            showShiftBoundaries: true
          },
          workHours: {
            enabled: true,
            highlightNonWorkingHours: true,
            nonWorkingColor: '#f5f5f5',
            nonWorkingOpacity: 0.5
          },
          resourceAvailability: {
            enabled: true,
            showUnavailablePeriods: true,
            unavailableColor: '#ffcdd2',
            unavailableOpacity: 0.5,
            showMaintenancePeriods: true,
            maintenanceColor: '#ff9800'
          }
        }
      };

      const tooltipConfiguration = {
        enabled: tooltipEnabled,
        trigger: tooltipTrigger,
        delay: tooltipDelay,
        content: {
          template: '',
          fields: [
            { field: 'operationName', label: 'Operation', visible: true, order: 1 },
            { field: 'orderNumber', label: 'Auftrag', visible: true, order: 2 },
            { field: 'plannedStart', label: 'Start', visible: true, order: 3, format: 'DD.MM.YYYY HH:mm' },
            { field: 'plannedEnd', label: 'Ende', visible: true, order: 4, format: 'DD.MM.YYYY HH:mm' },
            { field: 'duration', label: 'Dauer', visible: true, order: 5 }
          ],
          showImages: false,
          maxWidth: 300,
          maxHeight: 400
        },
        styling: {
          backgroundColor: '#ffffff',
          textColor: '#333333',
          borderColor: '#cccccc',
          borderWidth: 1,
          borderRadius: 4,
          fontSize: 12,
          fontFamily: 'Arial, sans-serif',
          padding: 8
        }
      };

      const interactionConfiguration = {
        allowTaskEditing,
        allowTaskMoving,
        allowTaskResizing,
        onClickAction,
        showContextMenu: false
      };

      const profileData: GanttVisualizationProfileDto = {
        ...profile,
        name: name.trim(),
        description: description.trim(),
        visibility,
        isDefault,
        colorConfiguration: colorConfiguration as any,
        resourceConfiguration: resourceConfiguration as any,
        displayConfiguration: displayConfiguration as any,
        tooltipConfiguration: tooltipConfiguration as any,
        interactionConfiguration: interactionConfiguration as any
      };

      // Save the profile and get the result
      let savedProfile: GanttVisualizationProfileDto;
      if (value?.uuid && !isClone) {
        // Update existing
        savedProfile = await GanttVisualizationProfileControllerService.update(value.uuid, profileData);
      } else {
        // Create new (or clone)
        savedProfile = await GanttVisualizationProfileControllerService.create(profileData);
      }

      // Set as default if checked
      if (isDefault && savedProfile?.uuid) {
        savedProfile = await GanttVisualizationProfileControllerService.setDefaultProfile(savedProfile.uuid);
      }

      // Convert to the expected type with id
      const savedProfileWithId: GanttProfileWithId = {
        ...savedProfile,
        id: savedProfile.uuid || ''
      };

      onSave(savedProfileWithId);
    } catch (error) {
      console.error('Failed to save profile:', error);
      alert(t('gantt.saveError', 'Fehler beim Speichern des Profils'));
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const visibilityOptions = [
    { text: t('gantt.visibility.user', 'Benutzer'), value: 'USER' },
    { text: t('gantt.visibility.mandant', 'Planungsbereich'), value: 'MANDANT' },
    { text: t('gantt.visibility.global', 'Global'), value: 'GLOBAL' }
  ];

  const colorStrategyOptions = [
    { text: t('gantt.colorStrategy.product', 'Produkt'), value: 'PRODUCT' },
    { text: t('gantt.colorStrategy.resource', 'Ressource'), value: 'RESOURCE' },
    { text: t('gantt.colorStrategy.order', 'Auftrag'), value: 'ORDER' }
  ];
  
  const defaultColorPalette = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFE66D',
    '#FF9FF3', '#54A0FF', '#5F27CD', '#00D2D3', '#FF9F43',
    '#10AC84', '#EE5A6F', '#C44569', '#F8B500', '#6C5CE7',
    '#A29BFE', '#FD79A8', '#FDCB6E', '#E17055', '#00B894'
  ];
  
  const getColorForMapping = (mapping: ColorMapping): string => {
    if (mapping.colorMode === 'MANUAL' && mapping.color) {
      return mapping.color;
    }
    // AUTO mode - use palette
    const index = mapping.autoColorIndex || 0;
    return defaultColorPalette[index % defaultColorPalette.length];
  };
  
  const updateColorMapping = (
    uuid: string,
    updates: Partial<ColorMapping>,
    mappings: ColorMapping[],
    setMappings: (mappings: ColorMapping[]) => void
  ) => {
    const updated = mappings.map(m => 
      m.uuid === uuid ? { ...m, ...updates } : m
    );
    setMappings(updated);
  };

  const resourceFilterOptions = [
    { text: t('gantt.resourceFilter.all', 'Alle'), value: 'ALL' },
    { text: t('gantt.resourceFilter.selected', 'Ausgewählte'), value: 'SELECTED' },
    { text: t('gantt.resourceFilter.byType', 'Nach Typ'), value: 'BY_TYPE' },
    { text: t('gantt.resourceFilter.byAttribute', 'Nach Attribut'), value: 'BY_ATTRIBUTE' }
  ];

  const taskLabelPositionOptions = [
    { text: t('gantt.taskLabelPosition.inside', 'Innerhalb'), value: 'INSIDE' },
    { text: t('gantt.taskLabelPosition.above', 'Oberhalb'), value: 'ABOVE' },
    { text: t('gantt.taskLabelPosition.below', 'Unterhalb'), value: 'BELOW' },
    { text: t('gantt.taskLabelPosition.tooltipOnly', 'Nur Tooltip'), value: 'TOOLTIP_ONLY' }
  ];

  const tooltipTriggerOptions = [
    { text: t('gantt.tooltipTrigger.hover', 'Hover'), value: 'HOVER' },
    { text: t('gantt.tooltipTrigger.click', 'Klick'), value: 'CLICK' },
    { text: t('gantt.tooltipTrigger.both', 'Beides'), value: 'BOTH' }
  ];

  const onClickActionOptions = [
    { text: t('gantt.onClickAction.none', 'Keine'), value: 'NONE' },
    { text: t('gantt.onClickAction.select', 'Auswählen'), value: 'SELECT' },
    { text: t('gantt.onClickAction.highlight', 'Hervorheben'), value: 'HIGHLIGHT' }
  ];

  return (
    <>
      <style>
        {`
          .gantt-profile-dialog .e-dlg-content {
            padding: 0 !important;
            overflow: hidden !important;
            display: flex !important;
            flex-direction: column !important;
          }
          .gantt-profile-tab-content {
            height: 100% !important;
            max-height: 100% !important;
            display: flex !important;
            flex-direction: column !important;
            overflow: hidden !important;
          }
          .gantt-profile-color-list-container {
            flex: 1 1 auto !important;
            min-height: 200px !important;
            max-height: calc(90vh - 400px) !important;
            display: flex !important;
            flex-direction: column !important;
            overflow: hidden !important;
          }
          .gantt-profile-color-list {
            flex: 1 1 auto !important;
            min-height: 0 !important;
            max-height: 100% !important;
            overflow-y: auto !important;
            overflow-x: hidden !important;
          }
          .gantt-profile-color-list::-webkit-scrollbar {
            width: 8px;
          }
          .gantt-profile-color-list::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
          }
          .gantt-profile-color-list::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 4px;
          }
          .gantt-profile-color-list::-webkit-scrollbar-thumb:hover {
            background: #555;
          }
          .gantt-profile-dialog .space-y-4 .e-checkbox-wrapper,
          .gantt-profile-dialog .space-y-2 .e-checkbox-wrapper {
            display: block !important;
            width: 100% !important;
          }
        `}
      </style>
      <DialogComponent
        visible={true}
        width="1000px"
        height="90vh"
        isModal={true}
        target="body"
        animationSettings={{ effect: 'None' }}
        cssClass="gantt-profile-dialog"
        zIndex={10000}
        header={value && !isClone ? t('gantt.editProfile', 'Profil bearbeiten') : t('gantt.createProfile', 'Profil erstellen')}
        showCloseIcon={true}
        closeOnEscape={true}
        close={onClose}
        footerTemplate={() => (
        <div className="flex justify-end gap-2 p-2">
          <ButtonComponent
            onClick={onClose}
            cssClass="e-outline"
            disabled={loading}
            type="button"
          >
            {t('common.cancel', 'Abbrechen')}
          </ButtonComponent>
          <ButtonComponent
            onClick={handleSave}
            cssClass="e-primary"
            disabled={loading}
            type="button"
          >
            {t('common.save', 'Speichern')}
          </ButtonComponent>
        </div>
      )}
    >
      <div style={{ height: 'calc(90vh - 150px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {loading && !profile ? (
          <div className="text-center py-8">{t('common.loading', 'Laden...')}</div>
        ) : (
          <ReactTabs
            style={{ flex: '1 1 0%' }}
            items={[
              // General tab
              {
                header: t('gantt.tab.general', 'Allgemein'),
                content: () => (
                  <div className="space-y-4 p-4" style={{ overflowY: 'auto' }}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('gantt.profileName', 'Profilname')} *
                    </label>
                    <TextBoxComponent
                      value={name}
                      change={(e) => {
                        setName(e.value || '');
                        setNameError(null);
                      }}
                      placeholder={t('gantt.profileNamePlaceholder', 'Name des Profils')}
                      cssClass="w-full"
                    />
                    {nameError && (
                      <div className="text-red-500 text-sm mt-1">{nameError}</div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('gantt.description', 'Beschreibung')}
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={t('gantt.descriptionPlaceholder', 'Beschreibung des Profils')}
                      className="w-full p-2 border border-gray-300 rounded"
                      rows={3}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('gantt.visibility', 'Sichtbarkeit')}
                    </label>
                    <DropDownListComponent
                      dataSource={visibilityOptions}
                      fields={{ text: 'text', value: 'value' }}
                      value={visibility}
                      change={(e) => setVisibility(e.value)}
                      enabled={!value?.uuid || isClone}
                      cssClass="w-full"
                    />
                  </div>

                  <div>
                    <CheckBoxComponent
                      label={t('gantt.setAsDefault', 'Als Standard setzen')}
                      checked={isDefault}
                      change={(e) => setIsDefault(e.checked || false)}
                    />
                  </div>
                  </div>
                ),
              },

              // Colour configuration tab
              {
                header: t('gantt.tab.color', 'Farben'),
                content: () => (
                  <div className="gantt-profile-tab-content p-4">
                    <div className="space-y-4 flex-shrink-0">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('gantt.colorStrategy', 'Farbstrategie')}
                        </label>
                        <DropDownListComponent
                          dataSource={colorStrategyOptions}
                          fields={{ text: 'text', value: 'value' }}
                          value={colorStrategy}
                          change={(e) => setColorStrategy(e.value)}
                          cssClass="w-full"
                        />
                      </div>

                      {colorStrategy === 'ORDER' && (
                        <div className="text-sm text-gray-600 p-4 bg-gray-50 rounded">
                          {t('gantt.orderColorInfo', 'Bei der Auftrags-Strategie werden die Farben automatisch basierend auf der Auftragsnummer zugewiesen. Eine manuelle Zuordnung ist für einzelne Aufträge nicht möglich.')}
                        </div>
                      )}

                      {colorStrategy === 'PRODUCT' && (
                        <div className="flex items-center justify-between">
                          <label className="block text-sm font-medium text-gray-700">
                            {t('gantt.productColors', 'Produkt-Farben')}
                          </label>
                          {loadingEntities && (
                            <span className="text-xs text-gray-500">{t('common.loading', 'Laden...')}</span>
                          )}
                        </div>
                      )}

                      {colorStrategy === 'RESOURCE' && (
                        <div className="flex items-center justify-between">
                          <label className="block text-sm font-medium text-gray-700">
                            {t('gantt.resourceColors', 'Ressourcen-Farben')}
                          </label>
                          {loadingEntities && (
                            <span className="text-xs text-gray-500">{t('common.loading', 'Laden...')}</span>
                          )}
                        </div>
                      )}
                    </div>

                    {colorStrategy === 'PRODUCT' && (
                      <div className="gantt-profile-color-list-container mt-2">
                        {productColorMappings.length > 0 ? (
                          <div className="gantt-profile-color-list border border-gray-200 rounded">
                            {productColorMappings.map((mapping) => (
                              <div key={mapping.uuid} className="flex items-center gap-2 p-2 border-b border-gray-200 hover:bg-gray-50">
                                <div 
                                  className="w-8 h-8 border border-gray-300 rounded flex-shrink-0"
                                  style={{ backgroundColor: getColorForMapping(mapping) }}
                                  title={mapping.name || t('gantt.unnamedProduct', 'Unbenanntes Produkt')}
                                />
                                <div className="flex-1 min-w-0 pr-2">
                                  <div className="text-sm text-gray-900">
                                    {mapping.name || t('gantt.unnamedProduct', 'Unbenanntes Produkt')}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <DropDownListComponent
                                    dataSource={[
                                      { text: t('gantt.colorMode.auto', 'Automatisch'), value: 'AUTO' },
                                      { text: t('gantt.colorMode.manual', 'Manuell'), value: 'MANUAL' }
                                    ]}
                                    fields={{ text: 'text', value: 'value' }}
                                    value={mapping.colorMode}
                                    change={(e) => updateColorMapping(mapping.uuid, { colorMode: e.value }, productColorMappings, setProductColorMappings)}
                                    cssClass="w-32"
                                  />
                                  {mapping.colorMode === 'MANUAL' && (
                                    <div className="flex gap-1">
                                      <input
                                        type="color"
                                        value={mapping.color || '#2196F3'}
                                        onChange={(e) => updateColorMapping(mapping.uuid, { color: e.target.value }, productColorMappings, setProductColorMappings)}
                                        className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                                        title={t('gantt.selectColor', 'Farbe auswählen')}
                                      />
                                      <TextBoxComponent
                                        value={mapping.color || '#2196F3'}
                                        change={(e) => updateColorMapping(mapping.uuid, { color: e.value || '#2196F3' }, productColorMappings, setProductColorMappings)}
                                        cssClass="w-24"
                                        placeholder="#2196F3"
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500 p-4 bg-gray-50 rounded">
                            {t('gantt.noProducts', 'Keine Produkte verfügbar')}
                          </div>
                        )}
                      </div>
                    )}

                    {colorStrategy === 'RESOURCE' && (
                      <div className="gantt-profile-color-list-container mt-2">
                        {resourceColorMappings.length > 0 ? (
                          <div className="gantt-profile-color-list border border-gray-200 rounded">
                            {resourceColorMappings.map((mapping) => (
                              <div key={mapping.uuid} className="flex items-center gap-2 p-2 border-b border-gray-200 hover:bg-gray-50">
                                <div 
                                  className="w-8 h-8 border border-gray-300 rounded flex-shrink-0"
                                  style={{ backgroundColor: getColorForMapping(mapping) }}
                                  title={mapping.name || t('gantt.unnamedResource', 'Unbenannte Ressource')}
                                />
                                <div className="flex-1 min-w-0 pr-2">
                                  <div className="text-sm text-gray-900">
                                    {mapping.name || t('gantt.unnamedResource', 'Unbenannte Ressource')}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <DropDownListComponent
                                    dataSource={[
                                      { text: t('gantt.colorMode.auto', 'Automatisch'), value: 'AUTO' },
                                      { text: t('gantt.colorMode.manual', 'Manuell'), value: 'MANUAL' }
                                    ]}
                                    fields={{ text: 'text', value: 'value' }}
                                    value={mapping.colorMode}
                                    change={(e) => updateColorMapping(mapping.uuid, { colorMode: e.value }, resourceColorMappings, setResourceColorMappings)}
                                    cssClass="w-32"
                                  />
                                  {mapping.colorMode === 'MANUAL' && (
                                    <div className="flex gap-1">
                                      <input
                                        type="color"
                                        value={mapping.color || '#2196F3'}
                                        onChange={(e) => updateColorMapping(mapping.uuid, { color: e.target.value }, resourceColorMappings, setResourceColorMappings)}
                                        className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                                        title={t('gantt.selectColor', 'Farbe auswählen')}
                                      />
                                      <TextBoxComponent
                                        value={mapping.color || '#2196F3'}
                                        change={(e) => updateColorMapping(mapping.uuid, { color: e.value || '#2196F3' }, resourceColorMappings, setResourceColorMappings)}
                                        cssClass="w-24"
                                        placeholder="#2196F3"
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500 p-4 bg-gray-50 rounded">
                            {t('gantt.noResources', 'Keine Ressourcen verfügbar')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ),
              },

              // Resource configuration tab
              {
                header: t('gantt.tab.resources', 'Ressourcen'),
                content: () => (
                  <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div className="space-y-4 p-4 flex-shrink-0">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('gantt.resourceFilter', 'Ressourcenfilter')}
                        </label>
                        <DropDownListComponent
                          dataSource={resourceFilterOptions}
                          fields={{ text: 'text', value: 'value' }}
                          value={resourceFilterType}
                          change={(e) => setResourceFilterType(e.value)}
                          cssClass="w-full"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {t('gantt.resourceFilterHint', 'Die Reihenfolge der Ressourcen wird über die Position in der Ressourcenverwaltung gesteuert.')}
                        </p>
                      </div>
                    </div>

                    {resourceFilterType !== 'ALL' && (
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '0 1rem 1rem 1rem', minHeight: 0 }}>
                        <div className="flex items-center justify-between mb-2 flex-shrink-0">
                          <label className="block text-sm font-medium text-gray-700">
                            {t('gantt.selectedResources', 'Ausgewählte Ressourcen')}
                          </label>
                          {loadingEntities && (
                            <span className="text-xs text-gray-500">{t('common.loading', 'Laden...')}</span>
                          )}
                        </div>
                        {resources.length > 0 ? (
                          <div style={{ height: 'calc(90vh - 450px)', minHeight: '400px', maxHeight: 'calc(90vh - 450px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <SelectableListComponent<ResourceWithSelection>
                              items={resources}
                              onSelectionChange={(updatedResources) => setResources(updatedResources)}
                              getItemId={(r) => r.uuid || ''}
                              getItemLabel={(r) => r.name || t('gantt.unnamedResource', 'Unbenannte Ressource')}
                              searchPlaceholder={t('common.search', 'Suchen...')}
                              searchFields={['name']}
                              height="100%"
                              showSelectAll={true}
                              showSummary={true}
                              showOnlySelectedFilter={true}
                              loading={loadingEntities}
                              emptyMessage={t('gantt.noResources', 'Keine Ressourcen verfügbar')}
                              noResultsMessage={t('gantt.noResources', 'Keine Ressourcen verfügbar')}
                            />
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500 p-4 bg-gray-50 rounded">
                            {t('gantt.noResources', 'Keine Ressourcen verfügbar')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ),
              },

              // Display configuration tab
              {
                header: t('gantt.tab.display', 'Anzeige'),
                content: () => (
                  <div className="space-y-4 p-4" style={{ overflowY: 'auto' }}>
                  <div>
                    <CheckBoxComponent
                      label={t('gantt.taskLabelEnabled', 'Vorgangsbeschriftung aktivieren')}
                      checked={taskLabelEnabled}
                      change={(e) => setTaskLabelEnabled(e.checked || false)}
                    />
                  </div>

                  {taskLabelEnabled && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('gantt.taskLabelTemplate', 'Beschriftungsvorlage')}
                        </label>
                        <TextBoxComponent
                          value={taskLabelTemplate}
                          change={(e) => setTaskLabelTemplate(e.value || '{operationName}')}
                          placeholder="{operationName}"
                          cssClass="w-full"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {t('gantt.taskLabelTemplateHint', 'Verfügbare Platzhalter: {operationName}, {orderNumber}, {materialName}')}
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('gantt.taskLabelPosition', 'Beschriftungsposition')}
                        </label>
                        <DropDownListComponent
                          dataSource={taskLabelPositionOptions}
                          fields={{ text: 'text', value: 'value' }}
                          value={taskLabelPosition}
                          change={(e) => setTaskLabelPosition(e.value)}
                          cssClass="w-full"
                        />
                      </div>
                    </>
                  )}

                  <div className="mt-4">
                    <CheckBoxComponent
                      label={t('gantt.statusIndicatorEnabled', 'Status-Indikator aktivieren')}
                      checked={statusIndicatorEnabled}
                      change={(e) => setStatusIndicatorEnabled(e.checked || false)}
                    />
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-4">
                      {t('gantt.timeScale', 'Zeitachse')}
                    </h3>
                    <div className="space-y-4">
                      <CheckBoxComponent
                        label={t('gantt.showWeekend', 'Wochenende anzeigen')}
                        checked={showWeekend}
                        change={(e) => setShowWeekend(e.checked || false)}
                      />
                      <CheckBoxComponent
                        label={t('gantt.showHolidays', 'Feiertage anzeigen')}
                        checked={showHolidays}
                        change={(e) => setShowHolidays(e.checked || false)}
                      />
                      <CheckBoxComponent
                        label={t('gantt.showShifts', 'Schichten anzeigen')}
                        checked={showShifts}
                        change={(e) => setShowShifts(e.checked || false)}
                      />
                    </div>
                  </div>
                  </div>
                ),
              },

              // Tooltip configuration tab
              {
                header: t('gantt.tab.tooltip', 'Tooltip'),
                content: () => (
                  <div className="space-y-4 p-4" style={{ overflowY: 'auto' }}>
                  <div>
                    <CheckBoxComponent
                      label={t('gantt.tooltipEnabled', 'Tooltip aktivieren')}
                      checked={tooltipEnabled}
                      change={(e) => setTooltipEnabled(e.checked || false)}
                    />
                  </div>

                  {tooltipEnabled && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('gantt.tooltipTrigger', 'Tooltip-Auslöser')}
                        </label>
                        <DropDownListComponent
                          dataSource={tooltipTriggerOptions}
                          fields={{ text: 'text', value: 'value' }}
                          value={tooltipTrigger}
                          change={(e) => setTooltipTrigger(e.value)}
                          cssClass="w-full"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('gantt.tooltipDelay', 'Verzögerung (ms)')}
                        </label>
                        <NumericTextBoxComponent
                          value={tooltipDelay}
                          change={(e) => setTooltipDelay(e.value || 300)}
                          min={0}
                          max={5000}
                          cssClass="w-full"
                        />
                      </div>
                    </>
                  )}
                  </div>
                ),
              },

              // Interaction configuration tab
              {
                header: t('gantt.tab.interaction', 'Interaktion'),
                content: () => (
                  <div className="space-y-4 p-4" style={{ overflowY: 'auto' }}>
                  <div className="space-y-4">
                    <CheckBoxComponent
                      label={t('gantt.allowTaskEditing', 'Vorgänge bearbeiten erlauben')}
                      checked={allowTaskEditing}
                      change={(e) => setAllowTaskEditing(e.checked || false)}
                    />
                    <CheckBoxComponent
                      label={t('gantt.allowTaskMoving', 'Vorgänge verschieben erlauben')}
                      checked={allowTaskMoving}
                      change={(e) => setAllowTaskMoving(e.checked || false)}
                    />
                    <CheckBoxComponent
                      label={t('gantt.allowTaskResizing', 'Vorgänge in Größe ändern erlauben')}
                      checked={allowTaskResizing}
                      change={(e) => setAllowTaskResizing(e.checked || false)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('gantt.onClickAction', 'Klick-Aktion')}
                    </label>
                    <DropDownListComponent
                      dataSource={onClickActionOptions}
                      fields={{ text: 'text', value: 'value' }}
                      value={onClickAction}
                      change={(e) => setOnClickAction(e.value)}
                      cssClass="w-full"
                    />
                  </div>
                  </div>
                ),
              },
            ]}
          />
        )}
      </div>
      </DialogComponent>
    </>
  );
};

// Default configurations
function getDefaultColorConfiguration() {
  return {
    colorStrategy: 'PRODUCT',
    defaultColorPalette: [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFE66D',
      '#FF9FF3', '#54A0FF', '#5F27CD', '#00D2D3', '#FF9F43',
      '#10AC84', '#EE5A6F', '#C44569', '#F8B500', '#6C5CE7',
      '#A29BFE', '#FD79A8', '#FDCB6E', '#E17055', '#00B894'
    ],
    statusColors: {
      PLANNED: '#2196F3',
      IN_PROGRESS: '#4CAF50',
      COMPLETED: '#8BC34A',
      DELAYED: '#F44336',
      CANCELLED: '#9E9E9E'
    }
  };
}

function getDefaultResourceConfiguration() {
  return {
    resourceFilter: {
      type: 'ALL'
    }
  };
}

function getDefaultDisplayConfiguration() {
  return {
    taskLabel: {
      enabled: true,
      template: '{operationName}',
      position: 'INSIDE',
      fontSize: 12,
      maxLength: 50
    },
    statusIndicator: {
      enabled: true,
      position: 'LEFT',
      size: 'SMALL',
      showBorder: true
    },
    progressIndicator: {
      enabled: false,
      type: 'BAR',
      color: '#4CAF50'
    },
    dependencyLines: {
      enabled: false,
      style: 'SOLID',
      color: '#666',
      showArrows: true,
      thickness: 1
    },
    timeScale: {
      interval: 60,
      showWeekend: true,
      showHolidays: true,
      showShifts: true
    },
    backgroundVisualization: {
      holidays: {
        enabled: true,
        displayMode: 'BACKGROUND_COLOR',
        color: '#ffebee',
        opacity: 0.5,
        showTooltip: true
      },
      shifts: {
        enabled: true,
        displayMode: 'BAND',
        opacity: 0.3,
        showShiftLabels: false,
        showShiftBoundaries: true
      },
      workHours: {
        enabled: true,
        highlightNonWorkingHours: true,
        nonWorkingColor: '#f5f5f5',
        nonWorkingOpacity: 0.5
      },
      resourceAvailability: {
        enabled: true,
        showUnavailablePeriods: true,
        unavailableColor: '#ffcdd2',
        unavailableOpacity: 0.5,
        showMaintenancePeriods: true,
        maintenanceColor: '#ff9800'
      }
    }
  };
}

function getDefaultTooltipConfiguration() {
  return {
    enabled: true,
    trigger: 'HOVER',
    delay: 300,
    content: {
      template: '',
      fields: [
        { field: 'operationName', label: 'Operation', visible: true, order: 1 },
        { field: 'orderNumber', label: 'Auftrag', visible: true, order: 2 },
        { field: 'plannedStart', label: 'Start', visible: true, order: 3, format: 'DD.MM.YYYY HH:mm' },
        { field: 'plannedEnd', label: 'Ende', visible: true, order: 4, format: 'DD.MM.YYYY HH:mm' },
        { field: 'duration', label: 'Dauer', visible: true, order: 5 }
      ],
      showImages: false,
      maxWidth: 300,
      maxHeight: 400
    },
    styling: {
      backgroundColor: '#ffffff',
      textColor: '#333333',
      borderColor: '#cccccc',
      borderWidth: 1,
      borderRadius: 4,
      fontSize: 12,
      fontFamily: 'Arial, sans-serif',
      padding: 8
    }
  };
}

function getDefaultInteractionConfiguration() {
  return {
    allowTaskEditing: false,
    allowTaskMoving: false,
    allowTaskResizing: false,
    onClickAction: 'SELECT',
    showContextMenu: false
  };
}

export default GanttProfileFormDialog;
